import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * "Validate address & regroup" — the preview, for ONE address group.
 *
 * Why a preview rather than one click that just fixes it: applying writes the
 * work order's shipping address, which is what prints on PDFs and parcel
 * labels. Rewriting that on a live order without showing it first is not
 * something to do on a button press.
 *
 * Why per-group rather than everything at once: this fans out to one UPS call
 * per work order. Validating every address on every load would be thousands of
 * calls to fix a handful of groups, so the user points it at the group that
 * actually looks wrong.
 *
 * Rows that UPS could not resolve are listed with the reason and CANNOT be
 * selected — an address UPS is unsure about is one a human still needs to
 * look at, and guessing at it would put a fabricated address on a real order.
 */

const addressText = (a) => {
  if (!a) return "—";
  const cityLine = [a.city, a.state, a.zip].filter(Boolean).join(" ");
  return [a.addressLine1, a.addressLine2, cityLine]
    .filter((l) => l && String(l).trim() !== "")
    .join(", ");
};

// The server caps a preview at 25 work orders, because each one is a separate
// UPS round trip. A whole invoice can hold more than that, so "Validate all"
// walks through in chunks rather than asking for a bigger cap.
const CHUNK = 25;

export default function AddressValidationModal({
  groups,
  onHide,
  onApplied,
  // Ask UPS again even for addresses that already have a stored answer. Only
  // the per-group Re-validate button sets this.
  force = false,
}) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  // woIds the user has ticked. Seeded from the rows that actually change.
  const [selected, setSelected] = useState({});

  const list = Array.isArray(groups) ? groups : [groups].filter(Boolean);
  const multi = list.length > 1;

  // Deduped: one work order can only be in one group, but flattening several
  // groups is exactly where a repeat would slip through and be validated twice.
  const woIds = [
    ...new Set(
      list
        .flatMap((g) => g?.orders || [])
        .map((o) => Number(o.woId))
        .filter((n) => Number.isInteger(n) && n > 0),
    ),
  ];
  const groupKey = list.map((g) => g?.key).join("|");

  const load = useCallback(async () => {
    if (!woIds.length) return;
    setLoading(true);
    setError(null);
    setProgress({ done: 0, total: woIds.length });
    try {
      const chunks = [];
      for (let i = 0; i < woIds.length; i += CHUNK) {
        chunks.push(woIds.slice(i, i + CHUNK));
      }

      // Sequential, not parallel: this is already one UPS call per work order,
      // and firing every chunk at once would multiply the burst for no gain
      // while making the progress count meaningless.
      const collected = [];
      for (const chunk of chunks) {
        const { data } = await API.post(
          "/bundle-sync/validate-addresses-preview",
          { woIds: chunk, force },
          { headers: authHeader() },
        );
        if (!data?.success) throw new Error(data?.message || "Validation failed");
        collected.push(...(data.data || []));
        setProgress({ done: collected.length, total: woIds.length });
      }

      setRows(collected);
      // Pre-tick everything that would change, so the common case is one
      // click. Anything UPS could not resolve is left out and cannot be
      // ticked at all.
      const next = {};
      collected.forEach((r) => {
        if (r.ok && r.changed) next[r.woId] = true;
      });
      setSelected(next);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setRows(null);
    }
    setLoading(false);
    setProgress(null);
    // woIds is rebuilt every render; groupKey is what actually identifies the
    // set being validated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey, force]);

  useEffect(() => {
    load();
  }, [load]);

  const changedRows = (rows || []).filter((r) => r.ok && r.changed);
  const unchangedRows = (rows || []).filter((r) => r.ok && !r.changed);
  const failedRows = (rows || []).filter((r) => !r.ok);
  // Answered from the stored record rather than UPS. Worth counting
  // separately: a modal that returns instantly with nothing to do looks
  // broken unless it says why.
  const cachedRows = (rows || []).filter((r) => r.cached);
  const selectedCount = changedRows.filter((r) => selected[r.woId]).length;

  const apply = async () => {
    const updates = changedRows
      .filter((r) => selected[r.woId])
      .map((r) => ({ woId: r.woId, address: r.suggested }));
    if (!updates.length) return;

    setApplying(true);
    setError(null);
    try {
      const { data } = await API.post(
        "/bundle-sync/validate-addresses-apply",
        { updates },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Could not apply");
      onApplied(data.appliedCount || 0, data.skipped || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setApplying(false);
    }
  };

  return (
    <>
      <style>
        {`
          /* Bootstrap gives every modal the SAME z-index, so a second one
             opened from the first renders UNDER that first modal's backdrop
             and looks like it failed to open. Each level in Nova Shipping
             therefore claims its own rung -- details 100000/1, edit 100020/1,
             products 100030/1, zoom 100040/1 (see NovaShipping.jsx and
             WorkOrderProductsModal.jsx). This opens from the details modal,
             so it takes the next one up. */
          .ns-validate-backdrop { z-index: 100050; }
          .ns-validate-modal    { z-index: 100051; }
        `}
      </style>

      <Modal
        show
        onHide={applying ? undefined : onHide}
        size="xl"
        scrollable
        centered
        backdropClassName="ns-validate-backdrop"
        className="ns-validate-modal"
        contentClassName="shadow-lg"
      >
        <Modal.Header closeButton={!applying}>
          <Modal.Title className="h5 mb-0">
            Validate address{multi ? "es" : ""} &amp; regroup
            <span className="text-muted fs-6 ms-2">
              {multi
                ? `${list.length} addresses · ${woIds.length} work order${woIds.length === 1 ? "" : "s"}`
                : list[0]?.address?.addressCompany || list[0]?.customerName || ""}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading && (
            <div className="text-center text-muted py-4">
              Checking {woIds.length} address
              {woIds.length === 1 ? "" : "es"} with UPS…
              {progress && progress.total > CHUNK && (
                <div className="mt-2">
                  {progress.done} of {progress.total}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="alert alert-danger py-2 px-3">{error}</div>
          )}

          {!loading && rows && (
            <>
              <p className="text-muted" style={{ fontSize: 14 }}>
                UPS was asked for the postal form of each order&apos;s shipping
                address. Nothing has been changed yet — ticked rows are written
                only when you press Apply, and the group re-forms afterwards.
              </p>

              {changedRows.length > 1 && (
                <div className="d-flex align-items-center gap-2 mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={applying}
                    onClick={() => {
                      const next = {};
                      changedRows.forEach((r) => (next[r.woId] = true));
                      setSelected(next);
                    }}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={applying}
                    onClick={() => setSelected({})}
                  >
                    Select none
                  </button>
                  <span className="text-muted" style={{ fontSize: 14 }}>
                    {selectedCount} of {changedRows.length} selected
                  </span>
                </div>
              )}

              {changedRows.length > 0 && (
                <div className="table-responsive mb-3">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr
                        className="text-muted text-uppercase fw-semibold"
                        style={{ fontSize: 13 }}
                      >
                        <th style={{ width: 44 }}></th>
                        <th style={{ width: 90 }}>W-ID</th>
                        <th>Current</th>
                        <th>UPS suggests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changedRows.map((r) => (
                        <tr key={r.woId}>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={!!selected[r.woId]}
                              disabled={applying}
                              onChange={() =>
                                setSelected((p) => ({
                                  ...p,
                                  [r.woId]: !p[r.woId],
                                }))
                              }
                            />
                          </td>
                          <td className="text-nowrap fw-semibold">W-{r.woId}</td>
                          <td className="text-muted" style={{ fontSize: 14 }}>
                            {addressText(r.current)}
                          </td>
                          <td style={{ fontSize: 14 }}>
                            {addressText(r.suggested)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {changedRows.length === 0 && failedRows.length === 0 && (
                <div className="alert alert-success py-2 px-3 mb-3">
                  {cachedRows.length === rows.length ? (
                    <>
                      Already checked against UPS, and nothing about these
                      addresses has changed since — so they were not sent
                      again. Use <strong>Re-validate</strong> on the group to
                      force a fresh check.
                    </>
                  ) : (
                    <>
                      Every address here is already in UPS&apos;s postal form.
                      There is nothing to change — if this group is still
                      split, the cause is not the address wording.
                    </>
                  )}
                </div>
              )}

              {/* Shown whenever anything came from the record, EXCEPT when the
                  all-clear above is already saying so.
                  It was first written as "only when the run was mixed", which
                  went quiet in the case that needs it most: every address
                  cached AND every one a failure. The all-clear does not render
                  then (there are failures), so nothing was left saying UPS had
                  not been called -- the run looked identical to a fresh one. */}
              {cachedRows.length > 0 &&
                !(changedRows.length === 0 && failedRows.length === 0) && (
                <div className="text-muted mb-3" style={{ fontSize: 14 }}>
                  {cachedRows.length} address
                  {cachedRows.length === 1 ? " was" : "es were"} checked
                  earlier and {cachedRows.length === 1 ? "was" : "were"} not
                  sent to UPS again.
                </div>
              )}

              {unchangedRows.length > 0 && changedRows.length > 0 && (
                <div className="text-muted mb-3" style={{ fontSize: 14 }}>
                  {unchangedRows.length} order
                  {unchangedRows.length === 1 ? " is" : "s are"} already correct
                  and will not be touched.
                </div>
              )}

              {failedRows.length > 0 && (
                <div className="alert alert-warning py-2 px-3 mb-0">
                  <div className="fw-semibold mb-1">
                    UPS could not resolve {failedRows.length} address
                    {failedRows.length === 1 ? "" : "es"} — left unchanged:
                  </div>
                  <ul className="mb-0 ps-3" style={{ fontSize: 14 }}>
                    {failedRows.map((r) => (
                      <li key={r.woId}>
                        <span className="fw-semibold">W-{r.woId}</span> — {r.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onHide}
            disabled={applying}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={applying || loading || selectedCount === 0}
            onClick={apply}
          >
            {applying
              ? "Applying…"
              : `Apply to ${selectedCount} order${selectedCount === 1 ? "" : "s"}`}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
